import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const ManagerAssignmentModal = ({ isOpen, onClose, request, onConfirm }) => {
  const [selectedManager, setSelectedManager] = useState('');
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchManagers();
    }
  }, [isOpen]);

  const fetchManagers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('personnel')
        ?.select('id, name, email, status')
        ?.eq('role', 'manager')
        ?.order('name');

      if (error) throw error;

      setManagers((data || [])?.map(m => ({
        value: m?.id,
        label: m?.name,
        description: `${m?.email} - ${m?.status === 'available' ? 'Available' : 'Busy'}`
      })));
    } catch (error) {
      console.error('Error fetching managers:', error?.message);
      setManagers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedManager) {
      alert('Please select a manager');
      return;
    }

    setIsAssigning(true);
    await onConfirm(request?.id, selectedManager);
    setIsAssigning(false);
    handleClose();
  };

  const handleClose = () => {
    setSelectedManager('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2100] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-md">
        <div className="bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="var(--color-secondary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Assign Manager</h2>
              <p className="text-sm text-muted-foreground">{request?.requestId}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-caption font-medium text-muted-foreground mb-3">Request Summary</h3>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer</p>
                <p className="font-medium text-foreground">{request?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Service Type</p>
                <p className="font-medium text-foreground">{request?.serviceType}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Priority</p>
                <p className="font-medium text-foreground capitalize">{request?.priority}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Loading managers...</span>
            </div>
          ) : (
            <Select
              label="Select Manager"
              placeholder="Choose a manager to assign"
              description="Manager will oversee this support request"
              required
              searchable
              options={managers}
              value={selectedManager}
              onChange={(value) => setSelectedManager(value)}
            />
          )}

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">Manager Role</p>
                <p className="text-sm text-muted-foreground">
                  The assigned manager will be responsible for overseeing this request, 
                  assigning technicians, and ensuring timely completion.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border-t border-border p-6 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            iconName="X"
            iconPosition="left"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            loading={isAssigning}
            disabled={!selectedManager}
            iconName="UserCheck"
            iconPosition="right"
            className="flex-1"
          >
            {isAssigning ? 'Assigning...' : 'Assign Manager'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManagerAssignmentModal;